/*!
 * SkyPlus Document Library Component - Clean Version
 * - No preview in properties panel (properties only like DigiWorks)
 * - Double-click opens Alfresco built-in viewer
 * - Fixed upload using HttpClient with proxy authentication
 * - Horizontal New Document/New Folder buttons
 * - No problematic AlfrescoApiService import
 */

import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Subject, firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NodesApiService, SearchService, FavoritesApiService } from '@alfresco/adf-content-services';
import { AuthenticationService, AppConfigService } from '@alfresco/adf-core';
import {  DocumentTypeConfig,  DocumentField,  getEnabledDocumentTypes} from '../indexing/document-types.config';
import { WfDocumentTypeRegistryService } from '../indexing/document-type-registry.service';
import { AlfrescoRepoBrowseService, RepoFolder } from '../wf-runtime/services/alfresco-repo-browse.service';
import { WfSearchApiService, WfSearchRequest } from '../search/wf-search-api.service';

interface FolderNode {
  id: string;
  name: string;
  parentId?: string;
  hasChildren: boolean;
  isExpanded: boolean;
  level: number;
  children: FolderNode[];
  isLoading?: boolean;
}

interface DocumentNode {
  id: string;
  name: string;
  isFolder: boolean;
  title?: string;
  description?: string;
  version?: string;
  status?: string;
  size?: number;
  sizeFormatted?: string;
  modifiedAt: Date;
  createdAt?: Date;
  modifiedBy: string;
  createdBy?: string;
  icon: string;
  iconColor: string;
  mimeType?: string;
  isSelected?: boolean;
  path?: string;
  isFavorite?: boolean;
  properties?: any;
  // Search navigation: parent folder info for "Go to folder" feature
  parentFolderId?: string;
  folderName?: string;
  pathElements?: { id: string; name: string }[];
}

interface BreadcrumbItem {
  id: string;
  name: string;
}

interface PropertyItem {
  label: string;
  value: string;
  icon?: string;
}

type ViewType = 'library' | 'recent' | 'favorites';

@Component({
  selector: 'sp-document-library',
  templateUrl: './skyplus-document-library.component.html',
  styleUrls: ['./skyplus-document-library.component.scss']
})
export class SkyPlusDocumentLibraryComponent implements OnInit, OnDestroy {
  
  // System folders to hide
  private readonly SYSTEM_FOLDERS = [
    'Data Dictionary', 'Sites', 'Imap Attachments', 'IMAP Home', 'Guest Home',
    'Shared', 'User Homes', 'Impress Templates', 'Scripts', 'Web Scripts',
    'Web Scripts Extensions', 'Models', 'Messages', 'Transfers',
    'Rendering Actions Space', 'Replication Actions Space', 'Email Templates',
    'RSS Templates', 'Activity Feed Email Templates', 'Workflow Notification',
    'Invite Email Templates', 'Node Templates', 'Space Templates', 'Saved Searches',
    'Scheduled Actions', 'Smart Folder Downloads', 'Smart Folder Templates', 'Solr Facets Space'
  ];
  
  viewType: ViewType = 'library';
  pageTitle = 'Document Library';
  pageSubtitle = 'Browse and manage your documents';
  
  // Search
  showAdvancedSearch = false;
  showDocTypeDropdown = false;
  searchMode: 'simple' | 'advanced' = 'simple';
  searchTerm = '';
  searchDocumentTypes: DocumentTypeConfig[] = [];
  selectedSearchDocType: DocumentTypeConfig | null = null;
  searchFieldValues: { [key: string]: any } = {};
  searchDateFrom = '';
  searchDateTo = '';
  isSearching = false;
  searchResults: DocumentNode[] = [];
  hasSearchResults = false;
  totalSearchResults = 0;

  currentFolderId = '-my-';
  currentFolderName = 'My Files';

  /** Configured library mode from app.config.json: 'home' | 'company' | 'site'. */
private libraryMode: 'home' | 'company' | 'site' = 'home';
/** Site id when libraryMode='site' (e.g. 'eacc'). */
private librarySiteId: string = '';
/** Resolved root nodeId — depends on libraryMode. */
libraryRootId: string = '-my-';
/** Display label for the resolved root. */
libraryRootLabel: string = 'My Files';

  isLoading = false;
  searchQuery = '';
  viewMode: 'list' | 'grid' = 'list';
  showSidebar = true;
  
  folderTree: FolderNode[] = [];
  documents: DocumentNode[] = [];
  breadcrumbs: BreadcrumbItem[] = [];

  // Folder-aware contextual search
  contextDocType: DocumentTypeConfig | null = null;
  /** Folder-context search starts collapsed so the list keeps the space. */
  contextSearchOpen = false;
  contextSearchValues: Record<string, any> = {};
  contextNameValue = '';
  contextScopeFolderId: string | null = null;
  currentFolderRepoPath = '';

  /** Field-value autocomplete suggestions, keyed by propertyName. */
  fieldSuggestions: Record<string, string[]> = {};
  private suggestTimer: any = null;
  /** Which engine answered the last search ('afts' | 'cmis') — shown in the banner. */
  searchEngineUsed = '';
  /** Non-null when the last search errored (distinct from a genuine "0 matches"). */
  searchError: string | null = null;

  /**
   * Unified list source: search results when a search is active, otherwise the
   * current folder's contents. Lets one table render both — no separate
   * stacked "results" panel above the listing.
   */
  get displayedDocuments(): DocumentNode[] {
    return this.hasSearchResults ? this.searchResults : this.documents;
  }
  
  totalItems = 0;
  pageSize = 25;
  currentPage = 1;
  pageSizeOptions = [10, 25, 50, 100];
  
  selectedItems: Set<string> = new Set();
  selectAll = false;
  
  // Properties Panel (NO preview - properties only)
  showPropertiesPanel = false;
  selectedDocument: DocumentNode | null = null;
  // Track what the current user is allowed to do on the
  // current folder and on the selected document, populated
  // from Alfresco's allowableOperations array.
  currentFolderAllowed: string[] = [];
  selectedDocAllowed:   string[] = [];

  // Move-to picker (reuses the studio's folder browser)
  moveOpen = false;
  moveLoading = false;
  moving = false;
  moveItems: RepoFolder[] = [];
  moveCrumbs: { id: string; name: string }[] = [{ id: '-root-', name: 'Company Home' }];

  // Properties-panel collapsible sections (collapsed by default)
  collapsedSections: Record<string, boolean> = { props: true, info: true };
  selectedDocLocked = false;

  documentProperties: PropertyItem[] = [];
  isLoadingProperties = false;
  activePropertiesTab: 'general' | 'custom' | 'versions' | 'permissions' = 'general';

  // General Properties (editable: name, title, description)
  generalProperties: { name: string; title: string; description: string } = {
    name: '',
    title: '',
    description: ''
  };
  editableGeneralProperties: { name: string; title: string; description: string } = {
    name: '',
    title: '',
    description: ''
  };
  isEditingGeneral = false;

  // Custom Properties (from custom aspects)
  customMetadataFields: { propertyName: string; label: string; value: any; type: string }[] = [];
  editableCustomProperties: { [key: string]: any } = {};
  isEditingCustom = false;

  // Read-only properties (type, size, version, created, modified, location)
  readOnlyProperties: PropertyItem[] = [];

  // Legacy support
  isEditMode = false;
  isSavingProperties = false;
  editableProperties: { [key: string]: any } = {};

  // Delete state
  isDeleting = false;
  
  
  // Embedded Document Viewer (like DigiWorks - same viewer, wrapped in theme)
  showViewer = false;
  viewerNodeId: string | null = null;
  viewerFileName = '';
  viewerFileVersion = '';
  viewerContentUrl = '';
  viewerMimeType = '';
  isUploading = false;
  uploadProgress = 0;
  
  subNavTabs = [
    { id: 'library', label: 'Document Library', route: '/documents', active: true },
    // { id: 'upload', label: 'Upload Documents', route: '/documents', active: false },
    // { id: 'templates', label: 'Templates', route: '/documents', active: false },
    { id: 'recent', label: 'Recent Documents', route: '/recent-files', active: false }
  ];
  
  private destroy$ = new Subject<void>();
  
  // Alfresco base URL - populated from app.config.json ecmHost
  private ecmHost = '';

  // Annotator viewer URL - populated from app.config.json annotatorUrl
  // (full viewer URL, e.g. "http://localhost:8092/annotator/viewer")
  private annotatorUrl = '';

  constructor(
    public router: Router,
    private route: ActivatedRoute,
    private nodesApi: NodesApiService,
    private searchService: SearchService,
    private favoritesApi: FavoritesApiService,
   // private contentService: ContentService,
    private authService: AuthenticationService,
    private appConfig: AppConfigService,
    private http: HttpClient,
    private registry: WfDocumentTypeRegistryService,
    private browse: AlfrescoRepoBrowseService,
    private searchApi: WfSearchApiService
  ) {
    // Get the ecmHost from app.config.json (e.g., "http://alfresco.local:8086")
    this.ecmHost = this.appConfig.get<string>('ecmHost', '') || '';
    // Remove trailing slash if present
    if (this.ecmHost.endsWith('/')) {
      this.ecmHost = this.ecmHost.slice(0, -1);
    }

    // Get the annotatorUrl from app.config.json
    // (full viewer URL, e.g. "http://localhost:8092/annotator/viewer")
    // Falls back to the dev default if the key is missing.
    this.annotatorUrl = this.appConfig.get<string>('annotatorUrl', '')
      || 'http://localhost:8092/annotator/viewer';
    if (this.annotatorUrl.endsWith('/')) {
      this.annotatorUrl = this.annotatorUrl.slice(0, -1);
    }

    // Read library root configuration
    const libraryConfig = this.appConfig.get<any>('skyplus.library', null);
    if (libraryConfig) {
      this.libraryMode    = libraryConfig.mode    || 'home';
      this.librarySiteId  = libraryConfig.siteId  || '';
    }
    console.log('[SkyPlusDocLib] library config:', {
      mode: this.libraryMode,
      siteId: this.librarySiteId
    });
  }

  
  
  /**
   * Build full Alfresco API URL using ecmHost from app.config.json.
   * Before Keycloak: relative URLs like /alfresco/api/... resolved to localhost:4200 (Angular dev server).
   * After Keycloak: must use full URL like http://alfresco.local:8086/alfresco/api/... (goes through nginx).
   */
  private getApiUrl(path: string): string {
    return `${this.ecmHost}${path}`;
  }

  /**
   * Get HTTP headers with Bearer token authentication.
   * With Keycloak, we use the OAuth access token instead of alf_ticket.
   */
  private getAuthHeaders(): HttpHeaders {
    const token = this.authService.getToken();
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  }

  async ngOnInit(): Promise<void> {
    // Resolve the configured library root before any folder loading
    await this.resolveLibraryRoot();
    this.route.data.pipe(takeUntil(this.destroy$)).subscribe(data => {
      this.viewType = data['viewType'] || 'library';
      this.updateViewState();
      this.loadContent();
    });
    
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      if (params['folderId'] && this.viewType === 'library') {
        this.navigateToFolder(params['folderId']);
      }
    });
    this.searchDocumentTypes = getEnabledDocumentTypes();
    this.registry.load().then(() => {
      this.searchDocumentTypes = getEnabledDocumentTypes();
    });

    // Restore folder context from a flip viewer return.
    const lastFolderId = sessionStorage.getItem('sp.lastFolderId');
    if (lastFolderId) {
      sessionStorage.removeItem('sp.lastFolderId');
      this.navigateToFolderById(lastFolderId);
    }
  }
  

/**
 * Resolve the configured library root before any folder loading happens.
 * For site mode, calls Alfresco's Sites API to look up the documentLibrary
 * nodeId. Falls back to '-my-' on error so the app stays usable.
 */
private async resolveLibraryRoot(): Promise<void> {
  if (this.libraryMode === 'site' && this.librarySiteId) {
    try {
      const url = this.getApiUrl(
        `/alfresco/api/-default-/public/alfresco/versions/1/sites/${this.librarySiteId}/containers/documentLibrary`
      );
      const response: any = await this.http.get(url, {
        headers: this.getAuthHeaders()
      }).toPromise();

      const nodeId = response?.entry?.id;
      if (nodeId) {
        this.libraryRootId    = nodeId;
        this.libraryRootLabel = `${this.librarySiteId.toUpperCase()} Documents`;
        console.log(`[SkyPlusDocLib] site mode → documentLibrary nodeId: ${nodeId}`);
      } else {
        throw new Error('Sites API returned no entry');
      }
    } catch (err) {
      console.error(`[SkyPlusDocLib] failed to resolve site '${this.librarySiteId}', falling back to home:`, err);
      this.libraryRootId    = '-my-';
      this.libraryRootLabel = 'My Files';
    }
  } else if (this.libraryMode === 'company') {
    this.libraryRootId    = '-root-';
    this.libraryRootLabel = 'Company Home';
  } else {
    this.libraryRootId    = '-my-';
    this.libraryRootLabel = 'My Files';
  }

  // Seed the current folder + name from the resolved root
  this.currentFolderId   = this.libraryRootId;
  this.currentFolderName = this.libraryRootLabel;
}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    // Revoke any active blob URL to prevent memory leaks
    if (this.viewerContentUrl && this.viewerContentUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.viewerContentUrl);
    }
  }
  
  private isSystemFolder(name: string): boolean {
    return this.SYSTEM_FOLDERS.includes(name);
  }
  
  private updateViewState(): void {
    this.subNavTabs.forEach(tab => tab.active = tab.id === this.viewType);
    
    switch (this.viewType) {
      case 'recent':
        this.pageTitle = 'Recent Documents';
        this.pageSubtitle = 'Documents modified in the last 30 days';
        this.showSidebar = false;
        break;
      case 'favorites':
        this.pageTitle = 'My Favourites';
        this.pageSubtitle = 'Your starred documents and folders';
        this.showSidebar = false;
        break;
      default:
        this.pageTitle = 'Document Library';
        this.pageSubtitle = 'Browse and manage your documents';
        this.showSidebar = true;
    }
  }
  
  private loadContent(): void {
    switch (this.viewType) {
      case 'recent': this.loadRecentDocuments(); break;
      case 'favorites': this.loadFavorites(); break;
      default:
        this.loadFolderTree();
        this.loadFolderContents(this.currentFolderId);
    }
  }
  

  
  // ===== RECENT DOCUMENTS =====
  
  async loadRecentDocuments(): Promise<void> {
    this.isLoading = true;
    this.breadcrumbs = [{ id: 'recent', name: 'Recent Documents' }];
    
    try {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      // Only the CURRENT user's recent documents (created or modified by them),
      // not every document changed in the repository.
      const me = (this.authService.getEcmUsername() || '').trim();
      const mine = me ? ` AND (cm:modifier:"${me}" OR cm:creator:"${me}")` : '';

      const searchRequest: any = {
        query: { query: `cm:modified:[${thirtyDaysAgo.toISOString()} TO NOW] AND TYPE:"cm:content"${mine}`, language: 'afts' },
        include: ['properties', 'path'],
        sort: [{ type: 'FIELD', field: 'cm:modified', ascending: false }],
        paging: { maxItems: this.pageSize, skipCount: (this.currentPage - 1) * this.pageSize }
      };
      
      const result = await this.searchService.searchByQueryBody(searchRequest).toPromise();
      this.totalItems = result.list?.pagination?.totalItems || 0;
      this.documents = (result.list?.entries || []).map((entry: any) => this.mapNodeToDocument(entry.entry, true));
    } catch (error) {
      console.error('Error loading recent documents:', error);
      this.documents = [];
    } finally {
      this.isLoading = false;
    }
  }
  
  // ===== FAVORITES =====
  
  async loadFavorites(): Promise<void> {
    this.isLoading = true;
    this.breadcrumbs = [{ id: 'favorites', name: 'My Favourites' }];
    
    try {
      const result = await this.favoritesApi.getFavorites('-me-', {
        skipCount: (this.currentPage - 1) * this.pageSize,
        maxItems: this.pageSize,
        where: '(EXISTS(target/file) OR EXISTS(target/folder))',
        include: ['properties', 'path']
      }).toPromise();
      
      this.totalItems = result.list?.pagination?.totalItems || 0;
      this.documents = (result.list?.entries || []).map((entry: any) => {
        const target = entry.entry.target;
        const node = target.file || target.folder;
        return this.mapNodeToDocument(node, !!target.file);
      });
    } catch (error) {
      console.error('Error loading favorites:', error);
      this.documents = [];
    } finally {
      this.isLoading = false;
    }
  }
  
  // ===== FOLDER TREE =====
  
  async loadFolderTree(): Promise<void> {
    try {
      const children = await this.nodesApi.getNodeChildren(this.libraryRootId, {
        where: '(isFolder=true)',
        orderBy: ['name ASC']
      }).toPromise();
      
      this.folderTree = (children.list?.entries || [])
        .filter((entry: any) => !entry.entry.name.startsWith('.') && !this.isSystemFolder(entry.entry.name))
        .map((entry: any) => ({
          id: entry.entry.id,
          name: entry.entry.name,
          hasChildren: true,
          isExpanded: false,
          level: 0,
          children: []
        }));
    } catch (error) {
      console.error('Error loading folder tree:', error);
      this.folderTree = [];
    }
  }
  
  async toggleFolderExpand(folder: FolderNode, event: Event): Promise<void> {
    event.stopPropagation();
    
    if (folder.isExpanded) {
      folder.isExpanded = false;
      return;
    }
    
    folder.isLoading = true;
    
    try {
      const children = await this.nodesApi.getNodeChildren(folder.id, {
        where: '(isFolder=true)',
        orderBy: ['name ASC']
      }).toPromise();
      
      folder.children = (children.list?.entries || [])
        .filter((entry: any) => !entry.entry.name.startsWith('.') && !this.isSystemFolder(entry.entry.name))
        .map((entry: any) => ({
          id: entry.entry.id,
          name: entry.entry.name,
          parentId: folder.id,
          hasChildren: true,
          isExpanded: false,
          level: folder.level + 1,
          children: []
        }));
      
      folder.hasChildren = folder.children.length > 0;
      folder.isExpanded = true;
    } catch (error) {
      console.error('Error loading folder children:', error);
    } finally {
      folder.isLoading = false;
    }
  }
  

  private navigateToFolderById(folderId: string): void {
  // Trust that the existing folder-load logic accepts a node id.
  // Adjust this call to whatever your existing "open folder" method is called.
  if (typeof (this as any).loadFolderContents === 'function') {
    (this as any).loadFolderContents(folderId);
    return;
  }
  if (typeof (this as any).openFolder === 'function') {
    (this as any).openFolder({ id: folderId } as any);
    return;
  }
  // If neither exists, the user will see the root and can navigate manually.
  console.warn('[Library] no folder-open method found, cannot restore', folderId);
}

  // ===== FOLDER CONTENTS =====
  
  async loadFolderContents(folderId: string): Promise<void> {
    this.isLoading = true;
    this.currentFolderId = folderId;
    this.closePropertiesPanel();
    
    try {
      const result = await this.nodesApi.getNodeChildren(folderId, {
        skipCount: (this.currentPage - 1) * this.pageSize,
        maxItems: this.pageSize,
        include: ['properties', 'path'],
        orderBy: ['isFolder DESC', 'name ASC']
      }).toPromise();
      
      const filteredEntries = (result.list?.entries || []).filter((entry: any) => {
        if (entry.entry.isFolder && this.isSystemFolder(entry.entry.name)) return false;
        return !entry.entry.name.startsWith('.');
      });
      
      const pg: any = result.list.pagination || {};
      this.totalItems = (pg.totalItems != null) ? pg.totalItems : filteredEntries.length;
      this.documents = filteredEntries.map((entry: any) => this.mapNodeToDocument(entry.entry, !entry.entry.isFolder));
      
      await this.loadBreadcrumbs(folderId);
    } catch (error) {
      console.error('Error loading folder contents:', error);
      this.documents = [];
    } finally {
      this.isLoading = false;
    }
  }
  
  private mapNodeToDocument(node: any, isFile: boolean = true): DocumentNode {
    const ext = node.name.split('.').pop()?.toLowerCase() || '';
    
    return {
      id: node.id,
      name: node.name,
      isFolder: !isFile && (node.isFolder !== undefined ? node.isFolder : !node.content),
      title: node.properties?.['cm:title'] || '',
      description: node.properties?.['cm:description'] || '',
      version: node.properties?.['cm:versionLabel'] || '1.0',
      status: 'Active',
      size: node.content?.sizeInBytes || 0,
      sizeFormatted: this.formatFileSize(node.content?.sizeInBytes || 0),
      modifiedAt: new Date(node.modifiedAt),
      createdAt: new Date(node.createdAt),
      modifiedBy: node.modifiedByUser?.displayName || node.modifiedByUser?.id || 'Unknown',
      createdBy: node.createdByUser?.displayName || node.createdByUser?.id || 'Unknown',
      icon: node.isFolder || (!isFile && !node.content) ? 'folder' : this.getDocumentIcon(ext),
      iconColor: node.isFolder || (!isFile && !node.content) ? '#FFC107' : this.getIconColor(ext),
      mimeType: node.content?.mimeType || '',
      path: node.path?.name || '',
      properties: node.properties || {}
    };
  }
  
  private getDocumentIcon(ext: string): string {
    const icons: Record<string, string> = {
      'pdf': 'picture_as_pdf', 'doc': 'description', 'docx': 'description',
      'xls': 'table_chart', 'xlsx': 'table_chart', 'ppt': 'slideshow', 'pptx': 'slideshow',
      'jpg': 'image', 'jpeg': 'image', 'png': 'image', 'gif': 'image', 'bmp': 'image',
      'mp4': 'movie', 'avi': 'movie', 'mov': 'movie', 'mkv': 'movie',
      'mp3': 'audiotrack', 'wav': 'audiotrack',
      'zip': 'folder_zip', 'rar': 'folder_zip', '7z': 'folder_zip',
      'txt': 'article', 'md': 'article', 'html': 'code', 'css': 'code', 'js': 'code'
    };
    return icons[ext] || 'insert_drive_file';
  }
  
  private getIconColor(ext: string): string {
    const colors: Record<string, string> = {
      'pdf': '#E53935', 'doc': '#1976D2', 'docx': '#1976D2',
      'xls': '#43A047', 'xlsx': '#43A047', 'ppt': '#FB8C00', 'pptx': '#FB8C00',
      'jpg': '#8E24AA', 'jpeg': '#8E24AA', 'png': '#8E24AA', 'gif': '#8E24AA',
      'mp4': '#E91E63', 'zip': '#795548'
    };
    return colors[ext] || '#607D8B';
  }
  
  // ===== BREADCRUMBS =====
  
  async loadBreadcrumbs(folderId: string): Promise<void> {
  try {
    // At the root — single breadcrumb
    if (folderId === this.libraryRootId) {
      this.breadcrumbs = [{ id: this.libraryRootId, name: this.libraryRootLabel }];
      this.currentFolderName = this.libraryRootLabel;
      this.currentFolderRepoPath = '';
      this.contextDocType = null;
      return;
    }

    const node = await this.nodesApi.getNode(folderId, { include: ['path', 'allowableOperations'] }).toPromise();
    this.currentFolderName = node.name;
    this.currentFolderAllowed = (node as any).allowableOperations || [];

    const pathElements = node.path?.elements || [];
    this.breadcrumbs = [{ id: this.libraryRootId, name: this.libraryRootLabel }];

    let startIndex = 0;
    if (this.libraryMode === 'site') {
      // Skip past Company Home/Sites/<siteId>/documentLibrary
      const docLibIdx = pathElements.findIndex((el: any) => el.name === 'documentLibrary');
      startIndex = docLibIdx >= 0 ? docLibIdx + 1 : 0;
    } else if (this.libraryMode === 'home') {
      // Skip past Company Home/User Homes/<username>
      const userHomesIdx = pathElements.findIndex((el: any) => el.name === 'User Homes');
      startIndex = userHomesIdx >= 0 ? userHomesIdx + 2 : 0;
    } else {
      // company mode: skip just Company Home
      const companyIdx = pathElements.findIndex((el: any) => el.name === 'Company Home');
      startIndex = companyIdx >= 0 ? companyIdx + 1 : 0;
    }

    for (let i = startIndex; i < pathElements.length; i++) {
      this.breadcrumbs.push({ id: pathElements[i].id, name: pathElements[i].name });
    }
    this.breadcrumbs.push({ id: node.id, name: node.name });

    // Folder-aware search: compute this folder's repo path from path.elements
    // (reliably populated, unlike path.name in some ADF versions) and match a type.
    const els: any[] = (node.path && node.path.elements) ? node.path.elements : [];
    const segs = els.map(e => e.name).filter(n => n && n !== 'Company Home');
    segs.push(node.name);
    this.currentFolderRepoPath = '/' + segs.join('/');
    this.detectContextType();
  } catch (error) {
    console.error('Error loading breadcrumbs:', error);
    this.breadcrumbs = [{ id: this.libraryRootId, name: this.libraryRootLabel }];
  }
}
  
  // ===== NAVIGATION =====
  
  navigateToFolder(folderId: string): void {
    this.currentPage = 1;
    this.selectedItems.clear();
    this.selectAll = false;
    this.loadFolderContents(folderId);
  }
  
  navigateToBreadcrumb(crumb: BreadcrumbItem): void {
    if (crumb.id === 'recent' || crumb.id === 'favorites') return;
    this.navigateToFolder(crumb.id);
  }
  
  goBack(): void {
    if (this.viewType !== 'library') {
      this.router.navigate(['/documents']);
      return;
    }
    if (this.breadcrumbs.length > 1) {
      const parentCrumb = this.breadcrumbs[this.breadcrumbs.length - 2];
      this.navigateToFolder(parentCrumb.id);
    }
  }
  
  goForward(): void {}
  goUp(): void { this.goBack(); }
  
  // ===== DOCUMENT ACTIONS =====
  
  // Single click - show properties panel (no preview)
  onItemClick(doc: DocumentNode, event?: Event): void {
    if (event) event.stopPropagation();
    this.showDocumentProperties(doc);
  }
  
  // Double click - open document in Alfresco viewer or navigate to folder
  onItemDoubleClick(doc: DocumentNode, event?: Event): void {
    if (event) event.stopPropagation();
    
    if (doc.isFolder) {
      if (this.viewType !== 'library') {
        this.router.navigate(['/documents', doc.id]);
      } else {
        this.navigateToFolder(doc.id);
      }
    } else {
      // Open in Alfresco built-in viewer
      this.openDocumentViewer(doc);
    }
  }
  
  // Open document in embedded ADF viewer (stays within SkyPlus theme)
  // Uses XHR to fetch content (sends Authorization header through nginx JWT validation),
  // then creates a blob URL for the viewer. This avoids the ?access_token= query param
  // approach which fails because nginx auth_request subrequests lose query params.
  async openDocumentViewer(doc: DocumentNode): Promise<void> {
    this.viewerNodeId = doc.id;
    this.viewerFileName = doc.name;
    this.viewerFileVersion = doc.version || '1.0';
    this.viewerMimeType = doc.mimeType || '';
    this.showViewer = true;
    this.showPropertiesPanel = false;

    try {
      const mimeType = doc.mimeType || '';
      const needsRendition = mimeType.includes('word') || 
                             mimeType.includes('msword') ||
                             mimeType.includes('openxmlformats') ||
                             mimeType.includes('spreadsheet') ||
                             mimeType.includes('presentation') ||
                             mimeType.includes('ms-excel') ||
                             mimeType.includes('ms-powerpoint');

      let contentApiUrl: string;

      if (needsRendition) {
        // Use Alfresco's PDF rendition for Office documents
        contentApiUrl = this.getApiUrl(
          `/alfresco/api/-default-/public/alfresco/versions/1/nodes/${doc.id}/renditions/pdf/content`
        );
      } else {
        contentApiUrl = this.getApiUrl(
          `/alfresco/api/-default-/public/alfresco/versions/1/nodes/${doc.id}/content?attachment=false`
        );
      }

      const blob = await this.http.get(contentApiUrl, {
        headers: this.getAuthHeaders(),
        responseType: 'blob'
      }).toPromise();

      if (blob) {
        this.viewerContentUrl = URL.createObjectURL(blob);
        this.viewerMimeType = needsRendition ? 'application/pdf' : (doc.mimeType || blob.type || '');
        console.log('Viewer blob URL created for:', doc.name, 'rendition:', needsRendition);
      }
    } catch (error) {
      console.error('Error loading document content:', error);
      this.viewerContentUrl = '';
    }
  }
  // async openDocumentViewer(doc: DocumentNode): Promise<void> {
  //   this.viewerNodeId = doc.id;
  //   this.viewerFileName = doc.name;
  //   this.viewerFileVersion = doc.version || '1.0';
  //   this.viewerMimeType = doc.mimeType || '';
  //   this.showViewer = true;
  //   this.showPropertiesPanel = false;

  //   try {
  //     // Fetch content via XHR — this sends the Authorization: Bearer header,
  //     // which nginx /alfresco/api/ validates via auth_request /auth-jwt correctly.
  //     const contentApiUrl = this.getApiUrl(
  //       `/alfresco/api/-default-/public/alfresco/versions/1/nodes/${doc.id}/content?attachment=false`
  //     );

  //     const blob = await this.http.get(contentApiUrl, {
  //       headers: this.getAuthHeaders(),
  //       responseType: 'blob'
  //     }).toPromise();

  //     if (blob) {
  //       // Create a local blob URL — no auth needed, browser can render directly
  //       this.viewerContentUrl = URL.createObjectURL(blob);
  //       this.viewerMimeType = doc.mimeType || blob.type || '';
  //       console.log('Viewer blob URL created for:', doc.name, 'mimeType:', this.viewerMimeType);
  //     }
  //   } catch (error) {
  //     console.error('Error loading document content:', error);
  //     // Fallback: try letting ADF viewer handle it via nodeId
  //     this.viewerContentUrl = '';
  //     console.log('Falling back to ADF viewer nodeId mode for:', doc.name);
  //   }
  // }
  
  // Close the document viewer and return to document list
  closeViewer(): void {
    // Revoke blob URL to free memory
    if (this.viewerContentUrl && this.viewerContentUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.viewerContentUrl);
    }
    this.showViewer = false;
    this.viewerNodeId = null;
    this.viewerFileName = '';
    this.viewerFileVersion = '';
    this.viewerContentUrl = '';
    this.viewerMimeType = '';
  }

  // ===== PROPERTIES PANEL (No Preview - Properties Only) =====
  
  async showDocumentProperties(doc: DocumentNode): Promise<void> {
    this.selectedDocument = doc;
    this.showPropertiesPanel = true;
    this.isLoadingProperties = true;
    this.activePropertiesTab = 'general';
    
    try {
      await this.loadDocumentProperties(doc.id);
    } catch (error) {
      console.error('Error loading properties:', error);
    } finally {
      this.isLoadingProperties = false;
    }
  }
  
  closePropertiesPanel(): void {
    this.showPropertiesPanel = false;
    this.selectedDocument = null;
    this.documentProperties = [];
    this.activePropertiesTab = 'general';
    this.isEditingGeneral = false;
    this.isEditingCustom = false;
    this.isEditMode = false;
    this.generalProperties = { name: '', title: '', description: '' };
    this.readOnlyProperties = [];
    this.customMetadataFields = [];
  }
  
  async loadDocumentProperties(nodeId: string): Promise<void> {
  try {
    const node = await this.nodesApi.getNode(nodeId, { include: ['properties', 'path', 'allowableOperations', 'isLocked'] }).toPromise();
    this.selectedDocAllowed = (node as any).allowableOperations || [];
    this.selectedDocLocked = (node as any).isLocked === true;
    
    // DEBUG: Log to see what properties exist
    console.log('Node properties:', node.properties);
    
    // General Properties (editable)
    this.generalProperties = {
      name: node.name || '',
      title: node.properties?.['cm:title'] || '',
      description: node.properties?.['cm:description'] || ''
    };
    
    // Read-only Properties
    this.readOnlyProperties = [
      { label: 'Type', value: node.content?.mimeType || 'Folder', icon: 'category' },
      { label: 'Size', value: this.formatFileSize(node.content?.sizeInBytes || 0), icon: 'straighten' },
      { label: 'Version', value: node.properties?.['cm:versionLabel'] || '1.0', icon: 'history' },
      { label: 'Created', value: new Date(node.createdAt).toLocaleString(), icon: 'event' },
      { label: 'Created By', value: node.createdByUser?.displayName || 'Unknown', icon: 'person' },
      { label: 'Modified', value: new Date(node.modifiedAt).toLocaleString(), icon: 'update' },
      { label: 'Modified By', value: node.modifiedByUser?.displayName || 'Unknown', icon: 'person_outline' },
      { label: 'Location', value: this.formatPath(node.path?.name), icon: 'folder_open' }
    ];
    
    // Standard properties for backward compatibility
    this.documentProperties = [
      { label: 'Name', value: node.name, icon: 'description' },
      { label: 'Title', value: node.properties?.['cm:title'] || '-', icon: 'title' },
      { label: 'Description', value: node.properties?.['cm:description'] || '-', icon: 'notes' },
      ...this.readOnlyProperties
    ];
    
    // Custom Properties (from custom aspects)
    this.customMetadataFields = [];
    const customProps = Object.keys(node.properties || {}).filter(k => 
      !k.startsWith('cm:') && !k.startsWith('sys:') && !k.startsWith('app:')
    );
    
    // DEBUG: Log custom properties
    console.log('Custom properties found:', customProps);
    
    customProps.forEach(key => {
      const value = node.properties[key];
      
      // Add to customMetadataFields for editing (even if empty)
      this.customMetadataFields.push({
        propertyName: key,
        label: this.formatPropertyLabel(key),
        value: value ?? '',
        type: this.getPropertyType(value)
      });
      
      // Also add to display properties (only if has value)
      if (value !== null && value !== undefined && value !== '') {
        this.documentProperties.push({
          label: this.formatPropertyLabel(key),
          value: String(value),
          icon: 'label'
        });
      }
    });
    
    // DEBUG: Log what we captured
    console.log('customMetadataFields:', this.customMetadataFields);
    
    // Initialize editable custom properties
    this.editableCustomProperties = {};
    this.customMetadataFields.forEach(field => {
      this.editableCustomProperties[field.propertyName] = field.value;
    });
    
    // Legacy support
    this.editableProperties = { ...this.editableCustomProperties };
    
  } catch (error) {
    console.error('Error loading document properties:', error);
  }
}


// ─── Permission helpers (drive *ngIf in template) ──────────────────
  // These mirror Alfresco's allowableOperations for the loaded
  // folder (currentFolderAllowed) or the selected document
  // (selectedDocAllowed).  When the current user has read-only
  // access, the relevant array is empty and the buttons hide.

  /** True if user can add files/folders into the current folder. */
  canUpload(): boolean {
    return this.currentFolderAllowed?.includes('create') ?? false;
  }

  /** True if user can delete items inside the current folder. */
  canDeleteInFolder(): boolean {
    return this.currentFolderAllowed?.includes('delete') ?? false;
  }

  /** True if user can edit the currently selected document. */
  canEditDoc(): boolean {
    return this.selectedDocAllowed?.includes('update') ?? false;
  }

  /** True if user can delete the currently selected document. */
  canDeleteDoc(): boolean {
    return this.selectedDocAllowed?.includes('delete') ?? false;
  }

  // ===== PROPERTIES PANEL UI =====
  toggleSection(key: string): void { this.collapsedSections[key] = !this.collapsedSections[key]; }
  isCollapsed(key: string): boolean { return !!this.collapsedSections[key]; }

  // ===== CHECK-OUT / CHECK-IN (Alfresco lock / unlock) =====
  async checkOut(): Promise<void> {
    if (!this.selectedDocument) { return; }
    try {
      const url = this.getApiUrl(
        `/alfresco/api/-default-/public/alfresco/versions/1/nodes/${this.selectedDocument.id}/lock`);
      await this.http.post(url,
        { timeToExpire: 0, type: 'ALLOW_OWNER_CHANGES', lifetime: 'PERSISTENT' },
        { headers: this.getAuthHeaders() }).toPromise();
      this.selectedDocLocked = true;
      await this.loadDocumentProperties(this.selectedDocument.id);
      alert('Checked out — locked for editing.');
    } catch (err: any) {
      alert('Check-out failed: ' + (err?.error?.error?.briefSummary || err?.message || 'error'));
    }
  }
  async checkIn(): Promise<void> {
    if (!this.selectedDocument) { return; }
    try {
      const url = this.getApiUrl(
        `/alfresco/api/-default-/public/alfresco/versions/1/nodes/${this.selectedDocument.id}/unlock`);
      await this.http.post(url, {}, { headers: this.getAuthHeaders() }).toPromise();
      this.selectedDocLocked = false;
      await this.loadDocumentProperties(this.selectedDocument.id);
      alert('Checked in — unlocked.');
    } catch (err: any) {
      alert('Check-in failed: ' + (err?.error?.error?.briefSummary || err?.message || 'error'));
    }
  }

  // ===== INDEX EXISTING DOCUMENT =====
  /** Open the indexing screen in edit mode for the selected document. */
  indexDocument(): void {
    if (!this.selectedDocument) { return; }
    this.router.navigate(['/indexing', this.selectedDocument.id],
      { queryParams: { from: this.currentFolderId } });
  }

  // ===== MOVE DOCUMENT =====
  openMove(): void {
    if (!this.selectedDocument) { return; }
    this.moveOpen = true;
    this.moveCrumbs = [{ id: '-root-', name: 'Company Home' }];
    this.loadMove('-root-');
  }
  closeMove(): void { this.moveOpen = false; }
  private loadMove(nodeId: string): void {
    this.moveLoading = true;
    this.browse.listFolders(nodeId)
      .then(items => { this.moveItems = items; })
      .catch(err => { console.error('Move browse failed:', err); this.moveItems = []; })
      .finally(() => { this.moveLoading = false; });
  }
  moveEnter(f: RepoFolder): void {
    this.moveCrumbs.push({ id: f.id, name: f.name });
    this.loadMove(f.id);
  }
  moveCrumbTo(i: number): void {
    this.moveCrumbs = this.moveCrumbs.slice(0, i + 1);
    this.loadMove(this.moveCrumbs[i].id);
  }
  async confirmMove(targetId: string, targetName: string): Promise<void> {
    if (!this.selectedDocument) { return; }
    if (targetId === '-root-') { alert('Pick a destination folder first.'); return; }
    this.moving = true;
    try {
      const url = this.getApiUrl(
        `/alfresco/api/-default-/public/alfresco/versions/1/nodes/${this.selectedDocument.id}/move`);
      await this.http.post(url, { targetParentId: targetId },
        { headers: this.getAuthHeaders() }).toPromise();
      this.moveOpen = false;
      this.closePropertiesPanel();
      await this.loadFolderContents(this.currentFolderId);
      alert(`Moved to ${targetName}.`);
    } catch (err: any) {
      console.error('Move failed:', err);
      const msg = err?.error?.error?.briefSummary || err?.message || 'permission or server error';
      alert(`Move failed: ${msg}`);
    } finally {
      this.moving = false;
    }
  }

// ============================================================================
// ADD IF MISSING: getPropertyType method
// ============================================================================

private getPropertyType(value: any): string {
  if (value === null || value === undefined) return 'text';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'boolean') return 'boolean';
  if (value instanceof Date || (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value))) return 'date';
  return 'text';
}
  
  private formatPropertyLabel(key: string): string {
    const parts = key.split(':');
    const name = parts.length > 1 ? parts[1] : parts[0];
    return name.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim();
  }

  // Enter edit mode for custom metadata
  enterEditMode(): void {
    this.isEditMode = true;
    // Reset editable properties to current values
    this.editableProperties = {};
    this.customMetadataFields.forEach(field => {
      this.editableProperties[field.propertyName] = field.value;
    });
  }
  
  // Cancel edit mode
  cancelEditMode(): void {
    this.isEditMode = false;
  }
  
  // Save edited properties
  async saveProperties(): Promise<void> {
    if (!this.selectedDocument) return;
    
    this.isSavingProperties = true;
    
    try {
      //const ticket = localStorage.getItem('ticket-ECM');
      let updateUrl = this.getApiUrl(`/alfresco/api/-default-/public/alfresco/versions/1/nodes/${this.selectedDocument.id}`);
      
      // Build properties object
      const properties: { [key: string]: any } = {};
      this.customMetadataFields.forEach(field => {
        const newValue = this.editableProperties[field.propertyName];
        // Only include if value has changed
        if (newValue !== field.value) {
          properties[field.propertyName] = newValue;
        }
      });
      
      if (Object.keys(properties).length > 0) {
        await this.http.put(updateUrl, { properties }, { headers: this.getAuthHeaders() }).toPromise();
        
        // Update local values
        this.customMetadataFields.forEach(field => {
          field.value = this.editableProperties[field.propertyName];
        });
        
        // Reload properties to refresh display
        await this.loadDocumentProperties(this.selectedDocument.id);
        
        // Refresh the document list
        await this.loadFolderContents(this.currentFolderId);
      }
      
      this.isEditMode = false;
      
    } catch (error) {
      console.error('Error saving properties:', error);
      alert('Failed to save properties. Please try again.');
    } finally {
      this.isSavingProperties = false;
    }
  }

  // ===== GENERAL PROPERTIES EDIT METHODS =====
  
  enterGeneralEditMode(): void {
    this.isEditingGeneral = true;
    this.editableGeneralProperties = { ...this.generalProperties };
  }

  cancelGeneralEdit(): void {
    this.isEditingGeneral = false;
  }

  async saveGeneralProperties(): Promise<void> {
    if (!this.selectedDocument) return;
    
    this.isSavingProperties = true;
    
    try {
     // const ticket = localStorage.getItem('ticket-ECM');
      let updateUrl = this.getApiUrl(`/alfresco/api/-default-/public/alfresco/versions/1/nodes/${this.selectedDocument.id}`);
      
      // Build update payload
      const payload: any = {
        properties: {
          'cm:title': this.editableGeneralProperties.title,
          'cm:description': this.editableGeneralProperties.description
        }
      };
      
      // Only include name if changed (requires different handling)
      if (this.editableGeneralProperties.name !== this.generalProperties.name) {
        payload.name = this.editableGeneralProperties.name;
      }
      
      console.log('Saving general properties:', payload);
      
      await this.http.put(updateUrl, payload, { headers: this.getAuthHeaders() }).toPromise();
      
      // Update local values
      this.generalProperties = { ...this.editableGeneralProperties };
      
      // Update selected document display
      if (this.selectedDocument) {
        this.selectedDocument.name = this.editableGeneralProperties.name;
        this.selectedDocument.title = this.editableGeneralProperties.title;
      }
      
      // Reload properties and document list
      await this.loadDocumentProperties(this.selectedDocument.id);
      await this.loadFolderContents(this.currentFolderId);
      
      this.isEditingGeneral = false;
      
    } catch (error) {
      console.error('Error saving general properties:', error);
      alert('Failed to save properties. Please try again.');
    } finally {
      this.isSavingProperties = false;
    }
  }

  // ===== CUSTOM PROPERTIES EDIT METHODS =====
  
  enterCustomEditMode(): void {
    this.isEditingCustom = true;
    // Reset editable properties to current values
    this.editableCustomProperties = {};
    this.customMetadataFields.forEach(field => {
      this.editableCustomProperties[field.propertyName] = field.value;
    });
  }

  cancelCustomEdit(): void {
    this.isEditingCustom = false;
  }

  async saveCustomProperties(): Promise<void> {
    if (!this.selectedDocument) return;
    
    this.isSavingProperties = true;
    
    try {
      //const ticket = localStorage.getItem('ticket-ECM');
      let updateUrl = this.getApiUrl(`/alfresco/api/-default-/public/alfresco/versions/1/nodes/${this.selectedDocument.id}`);
      
      // Build properties object
      const properties: { [key: string]: any } = {};
      this.customMetadataFields.forEach(field => {
        const newValue = this.editableCustomProperties[field.propertyName];
        properties[field.propertyName] = newValue;
      });
      
      console.log('Saving custom properties:', properties);
      
      await this.http.put(updateUrl, { properties }, { headers: this.getAuthHeaders() }).toPromise();
      
      // Update local values
      this.customMetadataFields.forEach(field => {
        field.value = this.editableCustomProperties[field.propertyName];
      });
      
      // Reload properties
      await this.loadDocumentProperties(this.selectedDocument.id);
      await this.loadFolderContents(this.currentFolderId);
      
      this.isEditingCustom = false;
      
    } catch (error) {
      console.error('Error saving custom properties:', error);
      alert('Failed to save properties. Please try again.');
    } finally {
      this.isSavingProperties = false;
    }
  }

  // ===== DELETE METHODS =====
  
  // Get selected documents
  getSelectedDocuments(): DocumentNode[] {
    return this.documents.filter(doc => doc.isSelected);
  }

  // Check if any documents are selected
  hasSelectedDocuments(): boolean {
    return this.documents.some(doc => doc.isSelected);
  }

  // Delete selected documents
  async deleteSelectedDocuments(): Promise<void> {
    const selectedDocs = this.getSelectedDocuments();
    
    if (selectedDocs.length === 0) {
      alert('Please select one or more documents to delete.');
      return;
    }
    
    const message = selectedDocs.length === 1
      ? `Are you sure you want to delete "${selectedDocs[0].name}"?`
      : `Are you sure you want to delete ${selectedDocs.length} items?`;
    
    if (!confirm(message)) {
      return;
    }
    
    this.isDeleting = true;
    
    try {
      //const ticket = localStorage.getItem('ticket-ECM');
      let successCount = 0;
      let failCount = 0;
      
      for (const doc of selectedDocs) {
        try {
          let deleteUrl = this.getApiUrl(`/alfresco/api/-default-/public/alfresco/versions/1/nodes/${doc.id}`);
          
          await this.http.delete(deleteUrl, { headers: this.getAuthHeaders() }).toPromise();
          successCount++;
          
        } catch (error) {
          console.error(`Error deleting ${doc.name}:`, error);
          failCount++;
        }
      }
      
      // Close properties panel if deleted document was selected
      if (this.selectedDocument && selectedDocs.some(d => d.id === this.selectedDocument?.id)) {
        this.closePropertiesPanel();
      }
      
      // Refresh the document list
      await this.loadFolderContents(this.currentFolderId);
      
      // Show result message
      if (failCount === 0) {
        alert(`Successfully deleted ${successCount} item(s).`);
      } else {
        alert(`Deleted ${successCount} item(s). Failed to delete ${failCount} item(s).`);
      }
      
    } catch (error) {
      console.error('Error during delete operation:', error);
      alert('An error occurred while deleting. Please try again.');
    } finally {
      this.isDeleting = false;
    }
  }

  // Delete single document (from context menu or properties panel)
  async deleteDocument(doc?: DocumentNode): Promise<void> {
    const document = doc || this.selectedDocument;
    
    if (!document) {
      return;
    }
    
    if (!confirm(`Are you sure you want to delete "${document.name}"?`)) {
      return;
    }
    
    this.isDeleting = true;
    
    try {
      //const ticket = localStorage.getItem('ticket-ECM');
      let deleteUrl = this.getApiUrl(`/alfresco/api/-default-/public/alfresco/versions/1/nodes/${document.id}`);
      
      await this.http.delete(deleteUrl, { headers: this.getAuthHeaders() }).toPromise();
      
      // Close properties panel if this document was selected
      if (this.selectedDocument?.id === document.id) {
        this.closePropertiesPanel();
      }
      
      // Refresh the document list
      await this.loadFolderContents(this.currentFolderId);
      
      alert(`"${document.name}" has been deleted.`);
      
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document. Please try again.');
    } finally {
      this.isDeleting = false;
    }
  }

  // ===== TAB METHODS =====
  
  setPropertiesTab(tab: 'general' | 'custom' | 'versions' | 'permissions'): void {
    this.activePropertiesTab = tab;
    // Cancel any active edit mode when switching tabs
    this.isEditingGeneral = false;
    this.isEditingCustom = false;
    this.isEditMode = false;
  }
  
  formatPath(path: string): string {
    if (!path) return '/';
    return path.replace('/Company Home/User Homes/', '~/').replace('/Company Home/', '/').replace(/^\/+/, '') || '/';
  }
  
  // ===== ACTIONS =====
  
  async downloadDocument(doc?: DocumentNode, event?: Event): Promise<void> {
    if (event) event.stopPropagation();
    const document = doc || this.selectedDocument;
    if (document && !document.isFolder) {
      try {
        // Fetch via XHR with auth header, then trigger download from blob
        const downloadUrl = this.getApiUrl(
          `/alfresco/api/-default-/public/alfresco/versions/1/nodes/${document.id}/content?attachment=true`
        );
        const blob = await this.http.get(downloadUrl, {
          headers: this.getAuthHeaders(),
          responseType: 'blob'
        }).toPromise();

        if (blob) {
          const blobUrl = URL.createObjectURL(blob);
          const link = window.document.createElement('a');
          link.href = blobUrl;
          link.download = document.name;
          window.document.body.appendChild(link);
          link.click();
          window.document.body.removeChild(link);
          URL.revokeObjectURL(blobUrl);
        }
      } catch (error) {
        console.error('Error downloading document:', error);
      }
    }
  }

  openInFlipViewer(): void {
  if (!this.viewerNodeId) return;
  this.router.navigate(['/documents', this.viewerNodeId, 'flip']);
}
  
  openInNewTab(): void {
    if (this.selectedDocument) {
      this.openDocumentViewer(this.selectedDocument);
    }
  }
  
  viewProperties(doc: DocumentNode, event: Event): void {
    event.stopPropagation();
    this.showDocumentProperties(doc);
  }
  
  // ===== SELECTION =====
  
  toggleSelectAll(): void {
    this.selectAll = !this.selectAll;
    if (this.selectAll) {
      this.documents.forEach(doc => { doc.isSelected = true; this.selectedItems.add(doc.id); });
    } else {
      this.documents.forEach(doc => doc.isSelected = false);
      this.selectedItems.clear();
    }
  }
  
  toggleItemSelection(doc: DocumentNode, event: Event): void {
    event.stopPropagation();
    doc.isSelected = !doc.isSelected;
    if (doc.isSelected) this.selectedItems.add(doc.id);
    else this.selectedItems.delete(doc.id);
    this.selectAll = this.selectedItems.size === this.documents.length;
  }
  
  // ===== PAGINATION =====
  
  onPageChange(page: number): void { this.currentPage = page; this.loadContent(); }
  onPageSizeChange(size: number): void { this.pageSize = size; this.currentPage = 1; this.loadContent(); }
  get totalPages(): number { return Math.ceil(this.totalItems / this.pageSize); }
  get startItem(): number { return (this.currentPage - 1) * this.pageSize + 1; }
  get endItem(): number { return Math.min(this.currentPage * this.pageSize, this.totalItems); }
  
  // ===== TOOLBAR ACTIONS =====
  
  // ── New Folder dialog (Round 6.2) ─────────────────────────────────────
  // Replaces the bare prompt(): folders are created with Name + Title
  // (both mandatory, so the library's TITLE column is never blank) and an
  // optional Description. Title defaults to the Name as you type until
  // it's edited. cm:title / cm:description land on the node at creation,
  // which auto-applies the cm:titled aspect.
  newFolderOpen = false;
  newFolderName = '';
  newFolderTitle = '';
  newFolderDescription = '';
  newFolderBusy = false;
  newFolderError: string | null = null;
  private newFolderTitleTouched = false;

  /** Characters Alfresco rejects in node names. */
  private static readonly ILLEGAL_NAME_CHARS = /["*\\><?/:|]/;

  createNewFolder(): void {
    if (this.viewType !== 'library') return;
    this.newFolderOpen = true;
    this.newFolderName = '';
    this.newFolderTitle = '';
    this.newFolderDescription = '';
    this.newFolderError = null;
    this.newFolderBusy = false;
    this.newFolderTitleTouched = false;
  }

  cancelNewFolder(): void {
    if (this.newFolderBusy) return;
    this.newFolderOpen = false;
  }

  /** Mirror the name into the title until the user edits the title. */
  onNewFolderNameChange(value: string): void {
    this.newFolderName = value;
    if (!this.newFolderTitleTouched) { this.newFolderTitle = value; }
  }

  onNewFolderTitleChange(value: string): void {
    this.newFolderTitle = value;
    this.newFolderTitleTouched = true;
  }

  /** Validation message for the current inputs, or null when valid. */
  get newFolderValidation(): string | null {
    const name = this.newFolderName.trim();
    if (!name) { return 'Folder name is required.'; }
    if (SkyPlusDocumentLibraryComponent.ILLEGAL_NAME_CHARS.test(name)) {
      return 'Folder name cannot contain " * \\ > < ? / : |';
    }
    if (name.endsWith('.')) { return 'Folder name cannot end with a dot.'; }
    if (!this.newFolderTitle.trim()) { return 'Title is required.'; }
    return null;
  }

  get newFolderValid(): boolean { return this.newFolderValidation === null; }

  confirmNewFolder(): void {
    if (!this.newFolderValid || this.newFolderBusy) return;
    this.newFolderBusy = true;
    this.newFolderError = null;

    const properties: Record<string, string> = {
      'cm:title': this.newFolderTitle.trim()
    };
    const desc = this.newFolderDescription.trim();
    if (desc) { properties['cm:description'] = desc; }

    this.nodesApi.createFolder(this.currentFolderId, {
      name: this.newFolderName.trim(),
      properties
    }).toPromise()
      .then(() => {
        this.newFolderBusy = false;
        this.newFolderOpen = false;
        this.loadFolderContents(this.currentFolderId);
        this.loadFolderTree();
      })
      .catch(err => {
        this.newFolderBusy = false;
        const detail = err?.error?.error?.briefSummary || err?.message || '';
        this.newFolderError = detail.includes('409') || err?.status === 409
          ? 'A folder with that name already exists here.'
          : ('Could not create the folder.' + (detail ? ' ' + detail : ''));
      });
  }
  
  // ===== FILE UPLOAD USING HttpClient =====
  
  triggerFileUpload(): void {
    if (this.viewType !== 'library') {
      this.router.navigate(['/documents']);
      return;
    }
    
    const input = window.document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.style.display = 'none';
    
    input.onchange = async (e: any) => {
      const files: FileList = e.target.files;
      if (files && files.length > 0) await this.uploadFiles(files);
      window.document.body.removeChild(input);
    };
    
    window.document.body.appendChild(input);
    input.click();
  }
  
  private async uploadFiles(files: FileList): Promise<void> {
    this.isUploading = true;
    this.uploadProgress = 0;
    
    const totalFiles = files.length;
    let uploadedCount = 0;
    
    // Store current folder to return to after upload (don't go to parent)
    const uploadFolderId = this.currentFolderId;
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      try {
        // Use FormData for multipart upload
        const formData = new FormData();
        formData.append('filedata', file, file.name);
        formData.append('name', file.name);
        formData.append('nodeType', 'cm:content');
        formData.append('autoRename', 'true');
        
        // Build URL with full ecmHost (goes through nginx with JWT validation)
        let uploadUrl = this.getApiUrl(`/alfresco/api/-default-/public/alfresco/versions/1/nodes/${uploadFolderId}/children`);
        
        const uploadResp: any = await this.http.post(uploadUrl, formData, { headers: this.getAuthHeaders() }).toPromise();

        // The file is now in Alfresco — count it and advance the progress bar NOW,
        // so the upload always completes and the document shows as uploaded.
        uploadedCount++;
        this.uploadProgress = Math.round((uploadedCount / totalFiles) * 100);

        // Foliation still happens — but as a BACKGROUND, best-effort request that must
        // never block the upload UI or the "uploaded" state. Fire-and-forget (subscribe,
        // not awaited): even if the backend is slow or the call fails, the file is already
        // uploaded and shown. (Previously this was awaited, so a slow backend froze the
        // progress bar at 0% even though the file had uploaded fine.)
        const newNodeId = uploadResp?.entry?.id;
        if (newNodeId) {
          const bpmBase = this.appConfig.get<string>('skyplus.apiUrl', '/bpm/api');
          this.http.post(`${bpmBase}/documents/foliate`,
            { nodeId: newNodeId, folderId: uploadFolderId },
            { headers: this.getAuthHeaders() })
            .subscribe({ next: () => {}, error: (folErr) => console.warn(`Background foliation failed for ${file.name}:`, folErr) });
        }
      } catch (error: any) {
        console.error(`Error uploading ${file.name}:`, error);
        if (error.status === 409) {
          alert(`File "${file.name}" already exists.`);
        } else if (error.status === 401) {
          alert(`Authentication error. Please log in again.`);
          this.router.navigate(['/login']);
          break;
        } else {
          alert(`Error uploading "${file.name}": ${error.message || 'Unknown error'}`);
        }
      }
    }
    
    this.isUploading = false;
    this.uploadProgress = 0;
    
    // Refresh the same folder we uploaded to (don't navigate away)
    this.loadFolderContents(uploadFolderId);
  }
  
  uploadDocuments(): void { this.triggerFileUpload(); }
  refreshFolder(): void { this.loadContent(); }

  openSelectedInFlipViewer(): void {
  if (!this.selectedDocument?.id) return;
  this.router.navigate(['/documents', this.selectedDocument.id, 'flip']);
}
  
  // ===== SEARCH =====
  
  onSearch(): void {
    if (this.searchQuery.trim()) this.router.navigate(['/search'], { queryParams: { q: this.searchQuery } });
  }
  
  // clearSearch(): void { this.searchQuery = ''; }
  setViewMode(mode: 'list' | 'grid'): void { this.viewMode = mode; }
  
  // ===== UTILITIES =====
  
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '-';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
  
  formatDate(date: Date): string {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / 86400000);
    
    if (days === 0) {
      const hours = Math.floor(diff / 3600000);
      if (hours === 0) {
        const mins = Math.floor(diff / 60000);
        return mins <= 1 ? 'Just now' : `${mins} mins ago`;
      }
      return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
    }
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }
  
  isFolderSelected(folderId: string): boolean { return this.currentFolderId === folderId; }
  
  onTabClick(tab: any): void {
    this.subNavTabs.forEach(t => t.active = false);
    tab.active = true;
    this.router.navigate([tab.route]);
  }

  // Add this method to open the indexing queue
  openIndexingQueue(): void {
    this.router.navigate(['/indexing'], {
      queryParams: { folderId: this.currentFolderId }
    });
  }

  editDocumentMetadata(doc: DocumentNode, event: Event): void {
    event.stopPropagation();
    this.router.navigate(['/indexing', doc.id]);
  }

  // ============================================================================
  // SEARCH METHODS
  // ============================================================================

  // Toggle advanced search panel
  toggleAdvancedSearch(): void {
    this.showAdvancedSearch = !this.showAdvancedSearch;
    if (!this.showAdvancedSearch) {
      this.showDocTypeDropdown = false;
    }
  }

  // Toggle document type dropdown
  toggleDocTypeDropdown(): void {
    this.showDocTypeDropdown = !this.showDocTypeDropdown;
  }

  // Select document type for advanced search
  selectSearchDocType(docType: DocumentTypeConfig | null): void {
    this.selectedSearchDocType = docType;
    this.searchFieldValues = {};
    
    if (docType) {
      docType.fields.forEach(field => {
        this.searchFieldValues[field.propertyName] = '';
      });
    }
  }

  /** Strike A — only fields flagged for search (undefined search = searchable, for legacy types). */
  searchableSearchFields(): DocumentField[] {
    return (this.selectedSearchDocType?.fields || []).filter(f => (f as DocumentField).search !== false);
  }

  // ===== DYNAMIC LIST COLUMNS (driven by the folder's document type) =====

  /**
   * Columns shown AFTER the Name column. When the current folder's document type has any field
   * flagged {@code column:true} (ticked in the Document Type studio, like the search flag), those
   * aspect fields become columns — plus a compact standard tail (Size / Modified / Modified By).
   * With no such type (or no ticked fields) it falls back to the standard columns, exactly as before.
   */
  extraColumns(): { key: string; label: string; kind: 'aspect' | 'std'; prop?: string }[] {
    const custom = (this.contextDocType?.fields || [])
      .filter(f => (f as DocumentField).column === true);
    if (custom.length) {
      return [
        ...custom.map(f => ({
          key: 'a:' + f.propertyName, label: f.label || f.propertyName, kind: 'aspect' as const, prop: f.propertyName
        })),
        { key: 'size', label: 'Size', kind: 'std' as const },
        { key: 'modified', label: 'Modified', kind: 'std' as const },
        { key: 'modifiedBy', label: 'Modified By', kind: 'std' as const }
      ];
    }
    return [
      { key: 'title', label: 'Title', kind: 'std' },
      { key: 'version', label: 'Version', kind: 'std' },
      { key: 'status', label: 'Status', kind: 'std' },
      { key: 'size', label: 'Size', kind: 'std' },
      { key: 'modified', label: 'Modified', kind: 'std' },
      { key: 'modifiedBy', label: 'Modified By', kind: 'std' }
    ];
  }

  /**
   * Grid template: checkbox (32px) + name + each extra column, all as user-resizable px widths,
   * then a flex filler so short tables don't leave a ragged gap (and wide ones scroll — the table
   * is overflow:auto). Widths persist in localStorage per column key.
   */
  get gridTemplate(): string {
    const nameW = this.colWidths['name'] ?? 240;
    const extras = this.extraColumns().map(c => (this.colWidths[c.key] ?? this.defaultColWidth(c)) + 'px');
    return `32px ${nameW}px ${extras.join(' ')} minmax(0, 1fr)`;
  }

  // ===== COLUMN RESIZE =====

  colWidths: Record<string, number> = this.loadColWidths();
  private resizingKey: string | null = null;
  private resizeStartX = 0;
  private resizeStartW = 0;
  get isColResizing(): boolean { return this.resizingKey !== null; }

  private loadColWidths(): Record<string, number> {
    try { return JSON.parse(localStorage.getItem('sp.docLib.colWidths') || '{}') || {}; }
    catch { return {}; }
  }

  /** Sensible starting width per column (px) before the user drags it. */
  defaultColWidth(col: { key: string }): number {
    switch (col.key) {
      case 'version':    return 70;
      case 'status':     return 90;
      case 'size':       return 80;
      case 'modified':   return 110;
      case 'modifiedBy': return 130;
      case 'title':      return 150;
      default:           return 140; // aspect columns
    }
  }

  /** Start dragging a column's right-edge handle. */
  startColResize(e: MouseEvent, key: string): void {
    e.preventDefault();
    e.stopPropagation();
    this.resizingKey = key;
    this.resizeStartX = e.clientX;
    this.resizeStartW = this.colWidths[key] ?? (key === 'name' ? 240 : this.defaultColWidth({ key }));
  }

  @HostListener('document:mousemove', ['$event'])
  onColResizeMove(e: MouseEvent): void {
    if (!this.resizingKey) { return; }
    const w = Math.max(50, this.resizeStartW + (e.clientX - this.resizeStartX));
    this.colWidths = { ...this.colWidths, [this.resizingKey]: w };
  }

  @HostListener('document:mouseup')
  onColResizeEnd(): void {
    if (!this.resizingKey) { return; }
    this.resizingKey = null;
    try { localStorage.setItem('sp.docLib.colWidths', JSON.stringify(this.colWidths)); } catch { /* ignore */ }
  }

  /** Cell text for a dynamic column (aspect property or a standard field). */
  cellText(doc: DocumentNode, col: { key: string; kind: 'aspect' | 'std'; prop?: string }): string {
    if (col.kind === 'aspect') {
      const v = (doc.properties || {})[col.prop!];
      return v === undefined || v === null || v === '' ? '-' : String(v);
    }
    switch (col.key) {
      case 'title':      return doc.title || '-';
      case 'version':    return doc.version || '-';
      case 'status':     return doc.status || 'Active';
      case 'size':       return doc.isFolder ? '-' : (doc.sizeFormatted || '-');
      case 'modified':   return this.formatDate(doc.modifiedAt);
      case 'modifiedBy': return doc.modifiedBy || '-';
      default:           return '-';
    }
  }

  // ===== FOLDER-AWARE CONTEXTUAL SEARCH =====

  /** Match the current folder to the most specific document type by baseFolderPath. */
  private detectContextType(): void {
    const path = (this.currentFolderRepoPath || '').replace(/\/+$/, '');
    let match: DocumentTypeConfig | null = null;
    let bestLen = -1;
    for (const dt of this.searchDocumentTypes) {
      const base = (dt.baseFolderPath || '').replace(/\/+$/, '');
      if (!base) { continue; }
      if (path === base || path.startsWith(base + '/')) {
        if (base.length > bestLen) { match = dt; bestLen = base.length; }
      }
    }
    const changed = this.contextDocType?.id !== match?.id;
    this.contextDocType = match;
    if (changed) { this.contextSearchValues = {}; this.contextNameValue = ''; this.contextSearchOpen = false; }
  }

  /** Search-flagged fields for the folder-context strip. */
  contextSearchableFields(): DocumentField[] {
    return (this.contextDocType?.fields || []).filter(f => (f as DocumentField).search !== false);
  }

  /**
   * Field-value autocomplete. On focus/typing in a metadata search input, fetch
   * the DISTINCT values already indexed for that property (folder-scoped) so the
   * user picks a real value instead of guessing — the root cause of "search
   * returns nothing". Debounced; degrades silently to free typing on any error.
   */
  onFieldSuggest(field: DocumentField, docTypeId?: string, folderId?: string | null): void {
    const prop = field.propertyName;
    if (!prop || field.type === 'select' || field.type === 'date' || field.type === 'number') { return; }
    const prefix = (this.contextSearchValues[prop] ?? this.searchFieldValues[prop] ?? '').toString().trim();
    if (this.suggestTimer) { clearTimeout(this.suggestTimer); }
    this.suggestTimer = setTimeout(() => {
      this.searchApi.values({
        documentTypeKey: docTypeId || this.contextDocType?.id || this.selectedSearchDocType?.id,
        aspectName: this.contextDocType?.aspectName || this.selectedSearchDocType?.aspectName,
        propertyName: prop,
        prefix: prefix || undefined,
        folderId: folderId === undefined ? (this.currentFolderId || undefined) : (folderId || undefined),
        includeSubfolders: true,
        maxItems: 15
      }).subscribe({
        next: r => { this.fieldSuggestions[prop] = r?.values || []; },
        error: () => { this.fieldSuggestions[prop] = []; }
      });
    }, 180);
  }

  /** Run the contextual search, scoped to the current folder subtree. */
  executeContextSearch(): void {
    if (!this.contextDocType) { return; }
    this.selectedSearchDocType = this.contextDocType;
    this.searchFieldValues = { ...this.contextSearchValues };
    this.searchDateFrom = '';
    this.searchDateTo = '';
    this.contextScopeFolderId = this.currentFolderId;
    this.isSearching = true;
    this.hasSearchResults = true;
    this.performAdvancedCmisSearch();
  }

  // Simple search from inline search box
  onSimpleSearchInput(_event: Event): void {
    // Optional: Can add debounce or suggestions here
  }

  executeSimpleSearch(): void {
    if (!this.searchTerm.trim()) {
      this.clearSimpleSearch();
      return;
    }
    
    this.isSearching = true;
    this.hasSearchResults = true;
    
    // Use the live search nodes API for better results
    this.performSimpleSearch(this.searchTerm.trim());
  }

  clearSimpleSearch(): void {
    this.searchTerm = '';
    this.searchResults = [];
    this.hasSearchResults = false;
    this.totalSearchResults = 0;
  }

  // Advanced search from panel
  executeAdvancedSearch(): void {
    this.contextScopeFolderId = null; // regular advanced search is repo-wide
    this.isSearching = true;
    this.hasSearchResults = true;
    
    this.performAdvancedCmisSearch();
  }

  clearAdvancedSearch(): void {
    this.selectedSearchDocType = null;
    this.searchFieldValues = {};
    this.searchDateFrom = '';
    this.searchDateTo = '';
    this.searchResults = [];
    this.hasSearchResults = false;
    this.totalSearchResults = 0;
    this.contextScopeFolderId = null;
  }

  /**
   * Clear for the folder-context search panel. The old inline handler only
   * emptied the input values — after a search had run, the results stayed
   * on screen and Clear looked dead. This resets the inputs AND the search
   * state so the normal folder listing returns immediately.
   */
  clearContextSearch(): void {
    this.contextSearchValues = {};
    this.contextNameValue = '';
    this.searchFieldValues = {};
    this.selectedSearchDocType = null;
    this.contextScopeFolderId = null;
    this.searchResults = [];
    this.hasSearchResults = false;
    this.totalSearchResults = 0;
    this.isSearching = false;
  }

  // Advanced search — Round 6: unified backend endpoint first.
  //
  // The legacy direct CMIS query combined `JOIN <aspect>` with
  // `IN_TREE(...)` — a combination NEITHER engine executes (Solr has no
  // CMIS JOIN; the database query path has no IN_TREE), which is why the
  // folder-scoped aspect search never returned results. The backend now
  // builds engine-appropriate queries (registry-resolved aspects + field
  // types, AFTS→CMIS fallback, folder scoping that works on both engines)
  // and returns raw node entries — the same shape this component already
  // maps. The legacy path below is retained as a transport fallback only.
  private async performAdvancedCmisSearch(): Promise<void> {
    try {
      const fields: Record<string, string> = {};
      const fieldTypes: Record<string, string> = {};
      (this.selectedSearchDocType?.fields || [])
        .filter(field => (field as DocumentField).search !== false)
        .forEach(field => {
          const value = this.searchFieldValues[field.propertyName];
          if (value && value.toString().trim() !== '') {
            fields[field.propertyName] = value.toString().trim();
            fieldTypes[field.propertyName] = field.type;
          }
        });

      const request: WfSearchRequest = {
        documentTypeKey: this.selectedSearchDocType?.id,
        aspectName: this.selectedSearchDocType?.aspectName,
        fields,
        fieldTypes,
        term: this.contextScopeFolderId ? (this.contextNameValue || '').trim() || undefined : undefined,
        dateFrom: this.searchDateFrom || undefined,
        dateTo: this.searchDateTo || undefined,
        folderId: this.contextScopeFolderId || undefined,
        includeSubfolders: true,
        maxItems: 100,
        skipCount: 0
      };

      const response = await firstValueFrom(this.searchApi.search(request));
      this.searchEngineUsed = response.engine || 'afts';
      this.searchError = null;
      console.log(`[doc-library] unified advanced search engine='${response.engine}' total=${response.totalItems}`);

      const allResults = (response.entries || []).map((node: any) => this.mapSearchResultToDocument(node));

      // Same system-path filtering as before (skipped for folder-scoped).
      this.searchResults = allResults.filter((doc: DocumentNode) => {
        if (this.contextScopeFolderId) { return true; }
        const path = doc.path || '';
        const isSystemFile = path.includes('/Data Dictionary') ||
                            path.includes('/Guest Home') ||
                            path.includes('/User Homes/');
        return !isSystemFile;
      });

      this.totalSearchResults = this.searchResults.length;
      this.isSearching = false;
      return;
    } catch (backendError) {
      console.warn('Unified advanced search unavailable — falling back to direct CMIS:', backendError);
    }
    await this.performAdvancedCmisSearchLegacy();
  }

  // Legacy direct-CMIS path — fallback only (older backend deployments).
  private async performAdvancedCmisSearchLegacy(): Promise<void> {
    try {
      //const ticket = localStorage.getItem('ticket-ECM');
      let searchUrl = this.getApiUrl('/alfresco/api/-default-/public/search/versions/1/search');
      
      // Build CMIS query
      const cmisQuery = this.buildAdvancedCmisQuery();
      
      const searchRequest = {
        query: {
          query: cmisQuery,
          language: 'cmis'
        },
        include: ['properties', 'path'],
        paging: {
          maxItems: 100,
          skipCount: 0
        }
      };
      
      console.log('Advanced CMIS query:', cmisQuery);
      console.log('Advanced CMIS request:', JSON.stringify(searchRequest));
      
      const response: any = await this.http.post(searchUrl, searchRequest, { headers: this.getAuthHeaders() }).toPromise();
      
      console.log('Advanced CMIS response:', response);
      
      const entries = response.list?.entries || [];
      
      const allResults = entries.map((entry: any) => {
        const node = entry.entry;
        return this.mapSearchResultToDocument(node);
      });
      
      console.log('All results before filter:', allResults);
      
      // Filter out system paths
      this.searchResults = allResults.filter((doc: DocumentNode) => {
        if (this.contextScopeFolderId) { return true; } // folder-scoped: keep all (incl. site folders)
        const path = doc.path || '';
        const isSystemFile = path.includes('/Data Dictionary') || 
                            path.includes('/Guest Home') ||
                            path.includes('/User Homes/');
        return !isSystemFile;
      });
      
      this.totalSearchResults = this.searchResults.length;
      
      console.log('Filtered advanced search results:', this.searchResults);
      
    } catch (error) {
      console.error('Advanced CMIS search error:', error);
      this.searchResults = [];
      this.totalSearchResults = 0;
    } finally {
      this.isSearching = false;
    }
  }

  // Build CMIS query for advanced search with aspects
  private buildAdvancedCmisQuery(): string {
    // If no document type selected, just search all documents
    if (!this.selectedSearchDocType) {
      // Build simple date-based query if dates are provided
      if (this.searchDateFrom || this.searchDateTo) {
        let query = `SELECT * FROM cmis:document WHERE `;
        const conditions: string[] = [];
        
        if (this.searchDateFrom) {
          conditions.push(`cmis:creationDate >= TIMESTAMP '${this.searchDateFrom}T00:00:00.000Z'`);
        }
        if (this.searchDateTo) {
          conditions.push(`cmis:creationDate <= TIMESTAMP '${this.searchDateTo}T23:59:59.999Z'`);
        }
        
        return query + conditions.join(' AND ');
      }
      
      return `SELECT * FROM cmis:document`;
    }
    
    // With aspect selected - use JOIN to query aspect properties
    // In CMIS, aspects are secondary types
    const aspectName = this.selectedSearchDocType.aspectName;
    
    // Build WHERE conditions for the aspect properties
    const whereConditions: string[] = [];
    
    this.selectedSearchDocType.fields
      .filter(field => (field as DocumentField).search !== false)
      .forEach(field => {
      const value = this.searchFieldValues[field.propertyName];
      if (value && value.toString().trim() !== '') {
        // In CMIS, property names use the full prefix (e.g., zim:claimDeptClaimNumber)
        const propName = field.propertyName;
        
        if (field.type === 'date') {
          whereConditions.push(`a.${propName} = TIMESTAMP '${value}T00:00:00.000Z'`);
        } else if (field.type === 'number') {
          whereConditions.push(`a.${propName} = ${value}`);
        } else if (field.type === 'select') {
          whereConditions.push(`a.${propName} = '${value}'`);
        } else {
          // Text field - use LIKE for partial match
          whereConditions.push(`a.${propName} LIKE '%${value}%'`);
        }
      }
    });
    
    // Add date range conditions if provided
    if (this.searchDateFrom) {
      whereConditions.push(`d.cmis:creationDate >= TIMESTAMP '${this.searchDateFrom}T00:00:00.000Z'`);
    }
    if (this.searchDateTo) {
      whereConditions.push(`d.cmis:creationDate <= TIMESTAMP '${this.searchDateTo}T23:59:59.999Z'`);
    }
    
    // Folder-aware: scope to the current folder subtree (and optional name match).
    if (this.contextScopeFolderId) {
      whereConditions.push(`IN_TREE(d, '${this.contextScopeFolderId}')`);
      if (this.contextNameValue && this.contextNameValue.trim() !== '') {
        whereConditions.push(`d.cmis:name LIKE '%${this.contextNameValue.trim()}%'`);
      }
    }

    // Build the JOIN query
    // SELECT d.* FROM cmis:document AS d JOIN zim:aspectName AS a ON d.cmis:objectId = a.cmis:objectId WHERE conditions
    let query = `SELECT d.* FROM cmis:document AS d JOIN ${aspectName} AS a ON d.cmis:objectId = a.cmis:objectId`;
    
    if (whereConditions.length > 0) {
      query += ` WHERE ${whereConditions.join(' AND ')}`;
    }
    
    console.log('Built advanced CMIS query:', query);
    return query;
  }

  // Simple search — Round 6: unified backend endpoint first (folders +
  // documents in one call; folders sorted first server-side). Falls back
  // to the legacy direct CMIS pair if the endpoint is unavailable.
  private async performSimpleSearch(term: string): Promise<void> {
    try {
      const response = await firstValueFrom(this.searchApi.search({
        term,
        includeFolders: true,
        maxItems: 100,
        skipCount: 0
      }));
      console.log(`[doc-library] unified simple search engine='${response.engine}' total=${response.totalItems}`);

      const allResults = (response.entries || []).map((node: any) =>
        this.mapSearchResultToDocument(node)
      );

      // Strip Alfresco system paths (same rule as before).
      this.searchResults = allResults.filter((doc: DocumentNode) => {
        const path = doc.path || '';
        return !path.includes('/Data Dictionary') &&
               !path.includes('/Guest Home') &&
               !path.includes('/User Homes/');
      });

      this.totalSearchResults = this.searchResults.length;
      this.isSearching = false;
      return;
    } catch (backendError) {
      console.warn('Unified simple search unavailable — falling back to direct CMIS:', backendError);
    }
    await this.performSimpleSearchLegacy(term);
  }

  // Legacy direct-CMIS path — fallback only (older backend deployments).
  private async performSimpleSearchLegacy(term: string): Promise<void> {
    try {
      const searchUrl = this.getApiUrl('/alfresco/api/-default-/public/search/versions/1/search');

      // Helper: fire a single CMIS search request
      const makeRequest = (query: string) =>
        this.http.post(searchUrl, {
          query: { query, language: 'cmis' },
          include: ['properties', 'path'],
          paging: { maxItems: 100, skipCount: 0 }
        }, { headers: this.getAuthHeaders() }).toPromise();

      // Search documents AND folders simultaneously
      const docQuery    = `select * from cmis:document where cmis:name like '%${term}%'`;
      const folderQuery = `select * from cmis:folder where cmis:name like '%${term}%'`;

      console.log('Simple search — doc query:', docQuery);
      console.log('Simple search — folder query:', folderQuery);

      const [docResponse, folderResponse]: [any, any] = await Promise.all([
        makeRequest(docQuery),
        // Folder search is non-fatal — if CMIS rejects it, docs still show
        makeRequest(folderQuery).catch(err => {
          console.warn('Folder CMIS search failed (non-fatal):', err);
          return { list: { entries: [] } };
        })
      ]);

      const docEntries: any[]    = docResponse?.list?.entries    || [];
      const folderEntries: any[] = folderResponse?.list?.entries || [];

      // Folders appear at the top of results (matching Windows Explorer convention)
      const allEntries = [...folderEntries, ...docEntries];

      const allResults = allEntries.map((entry: any) =>
        this.mapSearchResultToDocument(entry.entry)
      );

      console.log('All results before filter:', allResults);

      // Strip Alfresco system paths
      this.searchResults = allResults.filter((doc: DocumentNode) => {
        const path = doc.path || '';
        return !path.includes('/Data Dictionary') &&
               !path.includes('/Guest Home') &&
               !path.includes('/User Homes/');
      });

      this.totalSearchResults = this.searchResults.length;
      console.log('Filtered search results:', this.searchResults);

    } catch (error) {
      console.error('CMIS search error:', error);
      // Fallback to SQL API
      await this.performSqlSearchFallback(term);
    }

    this.isSearching = false;
  }

  // Fallback using Search SQL API
  private async performSqlSearchFallback(term: string): Promise<void> {
    try {
      //const ticket = localStorage.getItem('ticket-ECM');
      let searchUrl = this.getApiUrl('/alfresco/api/-default-/public/search/versions/1/sql');
      
      // SQL query
      const sqlQuery = `select * from alfresco where cm_name like '%${term}%'`;
      
      const searchRequest = {
        stmt: sqlQuery,
        locales: ['en_US'],
        timezone: 'UTC',
        includeMetadata: true,
        format: 'solr'
      };
      
      console.log('SQL fallback query:', sqlQuery);
      
      const response: any = await this.http.post(searchUrl, searchRequest, { headers: this.getAuthHeaders() }).toPromise();
      
      console.log('SQL fallback response:', response);
      
      const entries = response.list?.entries || response.entries || [];
      
      const allResults = entries.map((entry: any) => {
        const node = entry.entry || entry;
        return this.mapSqlResultToDocument(node);
      });
      
      // Filter out system paths
      this.searchResults = allResults.filter((doc: DocumentNode) => {
        const path = doc.path || '';
        const isSystemFile = path.includes('/Data Dictionary') || 
                            path.includes('/Guest Home') ||
                            path.includes('/User Homes/');
        return !isSystemFile;
      });
      
      this.totalSearchResults = this.searchResults.length;
      
    } catch (error) {
      console.error('SQL fallback error:', error);
      // Last resort: queries/nodes API
      await this.performQueriesNodesFallback(term);
    }
  }

  // Map SQL result to DocumentNode (SQL returns different format)
  private mapSqlResultToDocument(node: any): DocumentNode {
    const id = node.DBID || node.id || node['cm_noderef'] || '';
    const name = node.cm_name || node['cm:name'] || node.name || '';
    const isFolder = node.TYPE === 'folder' || node.isFolder || false;
    const ext = name.split('.').pop()?.toLowerCase() || '';
    
    return {
      id: id,
      name: name,
      isFolder: isFolder,
      title: node.cm_title || node['cm:title'] || '',
      description: node.cm_description || node['cm:description'] || '',
      mimeType: node.cm_content?.mimetype || '',
      size: node.cm_content?.size || 0,
      sizeFormatted: this.formatFileSize(node.cm_content?.size || 0),
      modifiedAt: new Date(node.cm_modified || node['cm:modified'] || Date.now()),
      modifiedBy: node.cm_modifier || node['cm:modifier'] || 'Unknown',
      createdAt: new Date(node.cm_created || node['cm:created'] || Date.now()),
      createdBy: node.cm_creator || node['cm:creator'] || 'Unknown',
      icon: isFolder ? 'folder' : this.getDocumentIcon(ext),
      iconColor: isFolder ? '#FFC107' : this.getIconColor(ext),
      path: node.PATH || node.path || '',
      properties: node
    };
  }

  // Last fallback using queries/nodes API
  private async performQueriesNodesFallback(term: string): Promise<void> {
    try {
      //const ticket = localStorage.getItem('ticket-ECM');
      
      let searchUrl = this.getApiUrl(`/alfresco/api/-default-/public/alfresco/versions/1/queries/nodes?term=${encodeURIComponent(term)}&rootNodeId=-root-&maxItems=100&include=path,properties`);
      
      console.log('Queries/nodes fallback URL:', searchUrl);
      
      const response: any = await this.http.get(searchUrl, { headers: this.getAuthHeaders() }).toPromise();
      
      console.log('Queries/nodes fallback response:', response);
      
      const allResults = (response.list?.entries || []).map((entry: any) => {
        const node = entry.entry;
        return this.mapSearchResultToDocument(node);
      });
      
      // Filter out system paths
      this.searchResults = allResults.filter((doc: DocumentNode) => {
        const path = doc.path || '';
        const isSystemFile = path.includes('/Data Dictionary') || 
                            path.includes('/Guest Home') ||
                            path.includes('/User Homes/');
        return !isSystemFile;
      });
      
      this.totalSearchResults = this.searchResults.length;
      
    } catch (error) {
      console.error('Queries/nodes fallback error:', error);
      this.searchResults = [];
      this.totalSearchResults = 0;
    }
  }

  private mapSearchResultToDocument(node: any): DocumentNode {
    const isFolder = node.isFolder || node.nodeType === 'cm:folder';
    const ext = node.name?.split('.').pop()?.toLowerCase() || '';

    // path.elements = [...ancestors, directParent]. Last element = direct parent folder.
    const pathElements: { id: string; name: string }[] = node.path?.elements || [];
    const parentElement = pathElements.length > 0 ? pathElements[pathElements.length - 1] : null;

    return {
      id: node.id,
      name: node.name,
      isFolder: isFolder,
      title: node.properties?.['cm:title'],
      description: node.properties?.['cm:description'],
      mimeType: node.content?.mimeType,
      size: node.content?.sizeInBytes,
      sizeFormatted: this.formatFileSize(node.content?.sizeInBytes || 0),
      modifiedAt: new Date(node.modifiedAt),
      modifiedBy: node.modifiedByUser?.displayName || 'Unknown',
      createdAt: new Date(node.createdAt),
      createdBy: node.createdByUser?.displayName || 'Unknown',
      icon: isFolder ? 'folder' : this.getDocumentIcon(ext),
      iconColor: isFolder ? '#FFC107' : this.getIconColor(ext),
      path: node.path?.name || '',
      properties: node.properties,
      // Parent folder info — drives "Go to folder" navigation in search results
      parentFolderId: node.parentId || parentElement?.id || '',
      folderName: parentElement?.name || '',
      pathElements: pathElements
    };
  }

  // Helper method for template - get first word of document type name
  getDocTypeName(name: string): string {
    return name ? name.split(' ')[0] : '';
  }

  closeSearchResults(): void {
    this.hasSearchResults = false;
    this.searchResults = [];
  }

  openSearchResult(doc: DocumentNode): void {
    if (doc.isFolder) {
      this.navigateToFolder(doc.id);
      this.showAdvancedSearch = false;
      this.clearAdvancedSearch();
      this.clearSimpleSearch();
    } else {
      this.openDocumentViewer(doc);
    }
  }

  /**
   * Navigate to the folder that CONTAINS the given search result.
   * Clears search, loads the parent folder contents, and attempts
   * to expand the sidebar tree so the user can see where they are.
   */
  async navigateToDocumentFolder(doc: DocumentNode, event?: Event): Promise<void> {
    if (event) event.stopPropagation();

    const parentId = doc.parentFolderId;
    if (!parentId) return;

    // Clear all search UI state
    this.clearSimpleSearch();
    this.clearAdvancedSearch();
    this.hasSearchResults = false;
    this.showAdvancedSearch = false;

    // Load the containing folder
    this.navigateToFolder(parentId);

    // Expand the sidebar tree to reveal this folder (best-effort, non-blocking)
    if (doc.pathElements && doc.pathElements.length > 0 && this.viewType === 'library') {
      this.expandFolderTreeToPath(doc.pathElements).catch(err =>
        console.warn('Could not auto-expand sidebar tree:', err)
      );
    }
  }

  /**
   * Lazily expands the sidebar folder tree along the given path so the
   * user can see which folder they navigated to after clicking "Go to folder".
   */
  private async expandFolderTreeToPath(
    pathElements: { id: string; name: string }[]
  ): Promise<void> {
    const hiddenNames = new Set<string>([
      'Company Home', 'User Homes', 'app:company_home', ...this.SYSTEM_FOLDERS
    ]);

    const relevant = pathElements.filter(el => !hiddenNames.has(el.name));
    if (relevant.length === 0) return;

    let currentLevel = this.folderTree;

    for (const element of relevant) {
      const treeNode = currentLevel.find(f => f.id === element.id);
      if (!treeNode) break;

      if (!treeNode.isExpanded) {
        treeNode.isLoading = true;
        try {
          const children = await this.nodesApi.getNodeChildren(treeNode.id, {
            where: '(isFolder=true)',
            orderBy: ['name ASC']
          }).toPromise();

          treeNode.children = (children.list?.entries || [])
            .filter((entry: any) =>
              !entry.entry.name.startsWith('.') && !this.isSystemFolder(entry.entry.name)
            )
            .map((entry: any) => ({
              id: entry.entry.id,
              name: entry.entry.name,
              parentId: treeNode.id,
              hasChildren: true,
              isExpanded: false,
              level: treeNode.level + 1,
              children: []
            }));

          treeNode.isExpanded = true;
        } catch (err) {
          console.error('Error loading children for tree expansion:', err);
          break;
        } finally {
          treeNode.isLoading = false;
        }
      }

      currentLevel = treeNode.children;
    }
  }


  signDocument(doc?: DocumentNode, event?: Event): void {
    if (event) event.stopPropagation();
    const document = doc || this.selectedDocument;
    if (!document || document.isFolder) return;

    this.openAnnotatorWindow(document.id);
  }

  /**
   * Opens the annotator popup. Opens synchronously (so the click gesture is
   * preserved and it isn't popup-blocked) to a loading page, then navigates to a
   * signed viewer URL when the backend has signed-access enabled, otherwise to the
   * legacy ?userId= URL (today's behaviour). Non-breaking: disabled / 404 / any
   * error all fall back to the legacy URL.
   */
  private openAnnotatorWindow(documentNodeId: string): Window | null {
    const userId = this.authService.getEcmUsername() || 'unknown';
    const legacyUrl = `${this.annotatorUrl}?docId=${documentNodeId}&userId=${userId}`;
    const features = 'width=1300,height=900,scrollbars=yes,resizable=yes';
    const popup = window.open('', 'SignDocument', features);
    if (popup) {
      try { popup.document.write('<!doctype html><title>Loading…</title><body style="font:14px sans-serif;padding:24px">Loading document…</body>'); } catch { /* ignore */ }
    }
    const go = (url: string) => { if (popup) { popup.location.href = url; } };
    const apiBase = this.appConfig.get<string>('skyplus.apiUrl', '/bpm/api');
    this.http.post<any>(`${apiBase}/signing/viewer-url`, { docId: documentNodeId, scope: 'annotate' }, { headers: this.getAuthHeaders() })
      .subscribe({
        next: (resp) => go(resp && resp.enabled && resp.token
          ? `${this.annotatorUrl}?docId=${encodeURIComponent(documentNodeId)}&token=${encodeURIComponent(resp.token)}`
          : legacyUrl),
        error: () => go(legacyUrl)
      });
    return popup;
  }
}